/**
 * adaptListinfoFields — DỊCH `listinfo.fields` SANG `data_list_config.fields`.
 *
 * Dùng cho các màn hình tra cứu chỉ tồn tại dưới dạng `listinfo` (`data_socai`, `data_sokho`,
 * `log_deleted`) để hiển thị được bằng DataListManager mà không phải tạo thêm bản ghi cấu hình cho từng
 * công ty. Đây cũng là mảnh đầu tiên của bộ chuyển đổi listinfo -> data_list_config trong lộ trình
 * thống nhất giao diện (xem docs/UI-MIGRATION-INPUTBASE-TO-DATALISTMANAGER.md).
 *
 * File KHÔNG import gì để test node chạy được trên chính mã nguồn này.
 */

// Field chỉ có ý nghĩa bố cục/hành động ở bộ cũ, không phải dữ liệu -> bỏ khi dịch.
const SKIP_TYPES = ['Link', 'Action', 'Group'];

/** Kiểu của listinfo -> kiểu của data_list_config. */
function mapFieldType(f) {
	const input = String((f && f.html_component_input) || '').toLowerCase();
	if (input === 'editor') return 'RichText';
	if (input === 'code') return 'Code';
	if (input === 'autocomplete' || input === 'picker') return 'Autocomplete';

	switch ((f && f.type) || 'String') {
		case 'Number': return 'Number';
		case 'Boolean': return 'Boolean';
		case 'Date': return 'Date';
		case 'DateTime': return 'DateTime';
		case 'Time': return 'Time';
		case 'Array': return 'ObjectArray';
		case 'Mixed': return 'Mixed';
		case 'Files': return 'FileArray';
		case 'File': case 'OtherFile': case 'Image': case 'Pdf': return 'File';
		default: return 'String';
	}
}

/** Chỉ nhận giá trị Boolean thật; chuỗi script của bộ cũ KHÔNG dịch được sang đây -> coi như không đặt. */
function boolOnly(v) {
	return v === true || v === 'true';
}

/**
 * Dịch MỘT field. Trả về null nếu field không nên xuất hiện ở bộ mới.
 * @param deps { applyGridConfigs, defaultScriptBody } — tiêm từ gridConfigs.js / fieldDefaults.js để
 *             file này không import gì. Không tiêm thì phần tương ứng bị bỏ qua chứ không ném:
 *             thiếu `applyGridConfigs` là mất `grid_configs`, thiếu `defaultScriptBody` là script
 *             `default` viết thiếu `return` bị coi như giá trị nguyên văn.
 */
function adaptListinfoField(f, deps) {
	if (!f || !f.name) return null;
	if (SKIP_TYPES.indexOf(f.type) >= 0) return null;

	const out = {
		field: f.name,
		label: f.header || f.name,
		type: mapFieldType(f),
	};
	//stt_col là đêt sắp xếp hiện thì trên table, stt là sắp xếp trên from
	//if (f.stt_col !== undefined || f.stt !== undefined) out.listOrder = Number(f.stt_col !== undefined ? f.stt_col : f.stt);
	out.listOrder = Number(f.stt||0);


	if (boolOnly(f.is_title)) out.isTitle = true;
	if (boolOnly(f.is_tmp)) out.isTemp = true;
	if (boolOnly(f.required)) out.required = true;
	if (boolOnly(f.not_display)) out.hideInList = true;
	if (boolOnly(f.not_input)) out.hideInForm = true;
	if (f.on_view === false) out.hideInView = true;
	if (String((f.html_component_input || '')).toLowerCase() === 'textarea') out.multiline = true;
	if (f.maxlength) out.maxLength = Number(f.maxlength);
	if (typeof f.min === 'number') out.min = f.min;
	if (typeof f.max === 'number') out.max = f.max;
	if (f.help_text) out.helpText = f.help_text;
	// TAB của bộ cũ -> tab của bố cục chứng từ. Tên tab giữ nguyên văn; việc "Thông tin chung" được coi
	// như không khai tab là luật của voucherFormTabs.js, không xử lý ở đây.
	if (f.tab && String(f.tab).trim()) out.formTab = String(f.tab).trim();
	if (f.group && String(f.group).trim()) out.formGroup = String(f.group).trim();
	if (f.multiple) out.multiple = true;

	

	// GIÁ TRỊ MẶC ĐỊNH. `default` của listinfo là một SCRIPT trả về giá trị (được phép `await`), tham số:
	// `master`, `API` (= context.apis, có asyncGetData…), `Moment`, `utils`, `token`
	// (= context.userInfo.token), `context`, `mediaQueryMatches` (true khi màn hình mobile). Bộ cũ TỰ
	// THÊM `return` nếu script không viết — `new Date()` chứ không phải `return new Date()`.
	//
	// data_list_config thì chỉ coi chuỗi CÓ chữ `return ` là script, còn lại là giá trị nguyên văn. Nên
	// phải chuẩn hoá NGAY TẠI ĐÂY: thiếu bước này thì `new Date()` được ghi thẳng vào bản ghi như một
	// CHUỖI — chứng từ mới có ngày là "new Date()" mà không một lỗi nào. Chuẩn hoá ở khâu dịch giữ được
	// cả hai hợp đồng: cấu hình viết tay của data_list_config KHÔNG đổi hành vi.
	// (Field `Action`/`Link` dùng `default` làm THÂN HÀNH ĐỘNG chứ không phải giá trị mặc định — chúng
	// đã bị loại từ `SKIP_TYPES` nên không lọt tới đây.)
	if (f.default !== undefined && f.default !== null && f.default !== '') {
		const dep = deps && deps.defaultScriptBody;
		out.default = typeof f.default === 'string'
			? (typeof dep === 'function' ? dep(f.default) : f.default)
			: f.default;
	}

	// SCRIPT CHẠY KHI FIELD ĐỔI GIÁ TRỊ. Giữ NGUYÊN VĂN — việc gán tên tham số do `parseDataListConfig`
	// lo (nó gán ĐỦ CẢ HAI bộ tên, xem lifecycleHooks.VALUE_CHANGED_ARGS). Script bộ cũ SỬA THẲNG vào
	// `master`/`detail` chứ không `return`, nên bên gọi phải lấy lại thay đổi từ chính object đã truyền
	// vào — xem DataListForm.handleFieldChange.
	if (typeof f.handle_value_changed === 'string' && f.handle_value_changed.trim()) {
		out.onValueChanged = f.handle_value_changed;
	}

	// FORM TÌM KIẾM: field tự dịch sang điều kiện (SearchForm.js: `new Function("condition","value",
	// "moment","numeral", handle_search_condition)`). Giữ nguyên văn; parseDataListConfig biên dịch với
	// đúng bộ tham số đó (+ formData, context ở cuối).
	if (typeof f.handle_search_condition === 'string' && f.handle_search_condition.trim()) {
		out.searchCondition = f.handle_search_condition;
	}

	// SỬA THẲNG TRÊN LƯỚI: phương ngữ của InputBase là `html_component_display = "input"`.
	// (InputBase còn có cờ `col_editable` cấp MÀN HÌNH, nhưng bộ mới cố ý chỉ nhận cấp CỘT —
	// xem inlineEdit.js — nên chỉ dịch cờ theo field này.)
	if (String(f.html_component_display || '').toLowerCase() === 'input') out.editableInList = true;

	// Lưới con của chứng từ: listinfo khai `type: 'Array'` + `form` = MÃ listinfo con. Giữ lại mã đó để
	// bên gọi nạp listinfo con và dựng `subFields`; không có subFields thì ObjectArray rỗng, tức chứng
	// từ mất sạch lưới chi tiết mà không có lỗi nào.
	if (out.type === 'ObjectArray' && f.form) out.detailForm = f.form;

	// Định dạng hiển thị — nay data_list_config đã có đủ các khoá tương ứng.
	if (f.format) out.format = f.format;
	if (f.align) out.align = f.align;
	if (f.color) out.color = f.color;
	if (boolOnly(f.bold)) out.bold = true;
	if (f.min_width_display) out.tableWidth = Number(f.min_width_display);

	// LIÊN KẾT MODEL: ref_* -> api/apiMapping/apiFields/apiCondition.
	if (f.ref_model && String(f.ref_model).indexOf('[') < 0) {
		out.api = f.ref_model;
		out.apiMapping = {
			value: f.ref_field || '_id',
			label: f.ref_label || f.ref_field || 'name',
		};
		if (f.ref_label_as) out.apiMapping.label_as = f.ref_label_as;
		if (f.ref_data_fields) out.apiFields = f.ref_data_fields;
		if (f.ref_condition) {
			try { out.apiCondition = JSON.parse(f.ref_condition); }
			catch (e) { /* điều kiện dạng script của bộ cũ: bỏ qua, không đoán bừa */ }
		}
		if (out.type !== 'Autocomplete' && out.type !== 'Select') out.type = 'Autocomplete';
	}

	// `grid_configs` — cỡ ô trên lưới form, readOnly, format… Chạy SAU CÙNG để mọi khoá khai tường minh
	// ở cấp field bên trên đã có mặt: grid_configs chỉ lấp chỗ trống chứ không giành chỗ.
	const applyGrid = deps && deps.applyGridConfigs;
	if (f.grid_configs && typeof applyGrid === 'function') {
		out.__gridConfigsResult = applyGrid(out, f.grid_configs);
	}

	return out;
}

/**
 * Dịch cả danh sách. `options.onlyDisplay` = true thì bỏ luôn field đã đánh dấu ẩn khỏi danh sách
 * (dùng cho màn hình tra cứu chỉ-đọc, khỏi kéo theo field không hiển thị).
 */
function adaptListinfoFields(fields, options, deps) {
	const opts = options || {};
	const out = [];


	for (const f of fields||[]) {
		const mapped = adaptListinfoField(f, deps);
		if (!mapped) continue;
		if (opts.onlyDisplay && mapped.hideInList) continue;
		out.push(mapped);
	}
	out.sort((a, b) => (a.listOrder === undefined ? 9999 : a.listOrder) - (b.listOrder === undefined ? 9999 : b.listOrder));
	// Gom báo cáo grid_configs ra ngoài rồi DỌN khoá tạm: nó chỉ để cảnh báo, không được lẫn vào cấu hình
	// field (parseDataListConfig duyệt đệ quy MỌI khoá, để sót là rác trôi xuống tận component).
	for (const fld of out) {
		if (!fld.__gridConfigsResult) continue;
		if (opts.gridReport) opts.gridReport.push({ field: fld.field, ...fld.__gridConfigsResult });
		delete fld.__gridConfigsResult;
	}
	return out;
}

export { SKIP_TYPES, mapFieldType, adaptListinfoField, adaptListinfoFields };
